import React, { useMemo, useState, useEffect } from "react";
import { connectAndMintPublic, detectTokenPocket, tokenPocketDeepLink, isMobileDevice } from "@/lib/caryConnect";
import { Zap, ShieldCheck, CheckCircle2, Loader2, Wallet as WalletIcon, Smartphone, RefreshCw, ExternalLink } from "lucide-react";

const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || "");

export default function WalletSync() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [slow, setSlow] = useState(false);
  const [tpReady, setTpReady] = useState(true);
  const expected = useMemo(() => new URLSearchParams(window.location.search).get("w") || "", []);
  const mobile = useMemo(() => isMobileDevice(), []);

  // Detect TokenPocket (EIP-6963 announces async, so re-check briefly).
  useEffect(() => {
    setTpReady(detectTokenPocket());
    const t = setTimeout(() => setTpReady(detectTokenPocket()), 600);
    return () => clearTimeout(t);
  }, []);

  const openInTokenPocket = () => { window.location.href = tokenPocketDeepLink(window.location.href); };

  // If TokenPocket is slow to pop (locked, or a wedged popup), reassure the user
  // and offer a reload instead of an endless silent spinner.
  useEffect(() => {
    if (!busy) { setSlow(false); return; }
    const t = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(t);
  }, [busy]);

  const connect = async () => {
    setBusy(true); setError("");
    try {
      const data = await connectAndMintPublic(expected || null);
      setDone(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not sync the wallet — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12" data-testid="wallet-sync-page">
      <div className="w-full max-w-[520px]">
        <div className="text-center mb-6">
          <div className="pill mx-auto inline-flex mb-4"><span className="dot" />BotChainCP · wallet sync</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Sync your CaryPact wallet.</h1>
          <p className="text-ink-2 mt-3 text-sm leading-relaxed">
            One tap shares your wallet's real mining, staking and team numbers with your community dashboard.
            No passwords, no seed phrases — you only approve a signature that proves the wallet is yours.
          </p>
        </div>

        <div className="glass-strong p-7" data-testid="wallet-sync-card">
          {done ? (
            <div className="text-center py-4" data-testid="wallet-sync-success">
              <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "#00863A" }} />
              <div className="font-display text-xl font-semibold">You're synced!</div>
              <p className="text-sm text-ink-2 mt-2">
                Wallet <b className="font-mono">{shortAddr(done.address)}</b> is connected and its full history is importing now.
                Your real numbers will feed the community dashboard within a few minutes. You can close this page.
              </p>
              {done.mismatch && (
                <div className="chip chip-gray mt-4 w-full !items-start text-left leading-relaxed" data-testid="wallet-sync-mismatch-note">
                  <span>
                    All done — the sync worked. Just so you know: the wallet requested from Wallets was <b className="font-mono">{shortAddr(done.expected)}</b>, and the wallet you synced from TokenPocket was <b className="font-mono">{shortAddr(done.address)}</b>. To sync {shortAddr(done.expected)} too, switch to it in TokenPocket and sync again.
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              {expected && (
                <div className="chip mb-4 w-full justify-center" style={{ background: "rgba(64,96,255,0.08)", color: "#4060FF" }} data-testid="wallet-sync-expected">
                  <WalletIcon size={13} /> Syncing wallet {shortAddr(expected)}
                </div>
              )}
              <ol className="space-y-3 text-sm text-ink-2 mb-6">
                <li className="flex gap-3"><Smartphone size={16} className="shrink-0 mt-0.5" style={{ color: "#4060FF" }} /> Open this page <b>inside the TokenPocket browser</b> (Discover → paste this link).</li>
                <li className="flex gap-3"><Zap size={16} className="shrink-0 mt-0.5" style={{ color: "#4060FF" }} /> Tap the button and choose the wallet you use with CaryPact.</li>
                <li className="flex gap-3"><ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: "#00863A" }} /> Approve the signature — it can't move funds, it only proves ownership.</li>
              </ol>
              <button onClick={connect} disabled={busy} className="btn-primary w-full !py-3.5 justify-center text-base" data-testid="wallet-sync-connect-btn">
                {busy ? <><Loader2 size={16} className="animate-spin" /> Waiting for your wallet…</> : <><Zap size={16} /> Connect &amp; sync my wallet</>}
              </button>
              {mobile && (
                <button onClick={openInTokenPocket} className="btn-secondary w-full !py-3 justify-center mt-3" data-testid="wallet-sync-open-tokenpocket-btn">
                  <ExternalLink size={15} /> Open in TokenPocket app
                </button>
              )}
              {!tpReady && (
                <p className="text-[11.5px] text-ink-3 mt-2 text-center leading-relaxed" data-testid="wallet-sync-tp-hint">
                  {mobile
                    ? <>TokenPocket isn't detected here. Tap <b>Open in TokenPocket app</b> to finish the sync inside TokenPocket.</>
                    : <>TokenPocket extension not detected. Install it and <b>unlock TokenPocket</b>, then tap Connect. On a phone? Open this same link inside the TokenPocket app browser.</>}
                </p>
              )}
              {busy && slow && (
                <div className="chip chip-gray mt-4 w-full !items-start text-left leading-relaxed" data-testid="wallet-sync-slow-hint">
                  <span>
                    No TokenPocket popup yet? Make sure TokenPocket is <b>open and unlocked</b> and check for its approval window (it can open behind this tab).
                    Still stuck? <button type="button" onClick={() => window.location.reload()} className="underline font-semibold inline-flex items-center gap-1" data-testid="wallet-sync-reload-btn"><RefreshCw size={12} /> reload &amp; try again</button>.
                  </span>
                </div>
              )}
              {error && <div className="chip chip-amber mt-4 w-full" data-testid="wallet-sync-error">{error}</div>}
            </>
          )}
        </div>

        <p className="text-center text-[11px] font-mono text-ink-3 mt-5">
          Your CaryPact session token is stored server-side and used only to read your history.
        </p>
      </div>
    </div>
  );
}
